import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import AddressSopDialog from './AddressSopDialog';
import { getAddressSops, deletePersonSop } from '../../services/sopService';
import { SOP_GRID_COLUMNS, SOP_DETAIL_FIELDS } from '../../constants/addressSopsTabConstants';
import InfoIcon from '@material-ui/icons/Info';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import LocalPrintshopIcon from '@material-ui/icons/LocalPrintshop';
import { buildSOPDetailPrintHTML } from '../../utility/addressSOPHelper';
import FormSpinner from 'components/FormSpinner';

const AddressSopsTab = ({ ptsAddressID, onSopsChanged }) => {
  const dispatch = useDispatch();
  const canEditAddress = usePermissions(PERMISSION_RESOURCES.ADDRESS).Edit;
  const [loading, setLoading] = useState(false);
  const [sopLinks, setSopLinks] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSopLink, setEditingSopLink] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const printIframeRef = useRef(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadSopLinks = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAddressSops(ptsAddressID),
      onSuccess: (data) => setSopLinks(data),
    });
  }, [dispatch, ptsAddressID]);

  const existingSopIds = useMemo(() => sopLinks.map((link) => link.ptsSOPID), [sopLinks]);

  const handleAdd = useCallback(() => {
    setEditingSopLink(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingSopLink(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingSopLink(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadSopLinks();
    onSopsChanged?.();
  }, [handleFormClose, loadSopLinks, onSopsChanged]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to remove this "${row.SOP?.SOPID || 'SOP'}" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () =>
              deletePersonSop({
                ptsParentID: ptsAddressID,
                ParentType: 'Address',
                ptsSOPID: row.ptsSOPID,
              }),
            onSuccess: () => {
              dispatch(notify('SOP deleted successfully', 'success'));
              loadSopLinks();
              onSopsChanged?.();
            },
          });
        },
        'Delete SOP'
      );
    },
    [dispatch, ptsAddressID, requestConfirm, loadSopLinks, onSopsChanged]
  );

  const handleViewDetails = useCallback((row) => setDetailTarget(row), []);
  const handleCloseDetails = useCallback(() => setDetailTarget(null), []);

  const handlePrint = useCallback(() => {
    if (!detailTarget) return;

    const html = buildSOPDetailPrintHTML(
      detailTarget,
      SOP_DETAIL_FIELDS,
      detailTarget.SOP?.SOPID || ''
    );
    const win = printIframeRef.current.contentWindow;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  }, [detailTarget]);

  useEffect(() => {
    loadSopLinks();
  }, [loadSopLinks]);

  return (
    <Box className="person-names-tab__grid-wrap address-sops-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add SOP"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditAddress}
        />
      </Box>

      <CommonDataGrid
        rows={sopLinks}
        columns={SOP_GRID_COLUMNS}
        getRowId={(row) => row.ptsSOPID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Address SOPs' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditAddress },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditAddress },
          custom: [
            {
              icon: <InfoIcon />,
              tooltip: 'View Details',
              onClick: handleViewDetails,
            },
          ],
        }}
      />

      {detailTarget && (
        <RMSCommonDialog
          open={Boolean(detailTarget)}
          title={detailTarget.SOPID || 'SOP Details'}
          onClose={handleCloseDetails}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}
          customButtons={
            <RMSButton
              label="Print"
              variant="contained"
              color="primary"
              prefixIcon={<LocalPrintshopIcon />}
              onClick={handlePrint}
            />
          }>
          <Grid container spacing={1}>
            {SOP_DETAIL_FIELDS.map(({ field, label, render, valueGetter }) => {
              const value = valueGetter ? valueGetter(detailTarget) : detailTarget[field];
              const display = render ? render(value) : value ?? '';
              return (
                <Grid item xs={6} key={field}>
                  <Typography variant="caption" color="textSecondary" component="div">
                    {label}
                  </Typography>
                  <Typography variant="body2">{display}</Typography>
                </Grid>
              );
            })}
          </Grid>

          <iframe
            ref={printIframeRef}
            title="address-party-print-frame"
            className="address-party-print-iframe"
          />
        </RMSCommonDialog>
      )}

      {formOpen && (
        <AddressSopDialog
          open={formOpen}
          ptsAddressID={ptsAddressID}
          sopLink={editingSopLink}
          existingSopIds={existingSopIds}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default AddressSopsTab;
